import { useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { User, Landmark, Wallet, LifeBuoy, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SETTINGS_LABEL_COPY } from "@fomofi/shared";
import { reportSignOutFailure } from "@/lib/signOutFailure";

// Upper-right profile dropdown (feedback #5/#6) — relocated from the sidebar
// footer. Subscription routes to the existing /payment placeholder until
// Stripe lands (#567); Support opens the static /help FAQ page. The theme
// toggle lives here (not as a standalone cluster button — it crowded the
// cluster and duplicated Settings' Appearance control on the same screen).
export function ProfileMenu() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useClerk();

  const displayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || "User";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const initials =
    user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || "U";
  const avatarUrl = user?.imageUrl;

  const avatar = avatarUrl ? (
    <img src={avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full" />
  ) : (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 text-[11px] font-bold text-white">
      {initials}
    </div>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open profile menu"
          className="flex h-9 items-center gap-2 rounded-lg border border-border bg-background pl-1.5 pr-2 text-muted-foreground transition hover:text-foreground"
        >
          {avatar}
          <span className="hidden max-w-[140px] truncate text-[13px] font-medium text-foreground sm:inline">
            {displayName}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-56" sideOffset={8}>
        <DropdownMenuLabel className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{displayName}</span>
          {email && (
            <span className="truncate text-xs font-normal text-muted-foreground">{email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")}>
          <User className="h-4 w-4" />
          {/* AN-46: the one name for that screen; "Settings" below it went to the same page. */}
          {SETTINGS_LABEL_COPY}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/banks")}>
          <Landmark className="h-4 w-4" />
          Connected accounts
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/payment")}>
          <Wallet className="h-4 w-4" />
          Subscription
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/help")}>
          <LifeBuoy className="h-4 w-4" />
          Support
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* FP-174: offline, sign-out now fails instead of pretending (it used to look done
            and the session came back). Say so, rather than silently doing nothing. */}
        <DropdownMenuItem onClick={() => void signOut().catch(reportSignOutFailure)}>
          <LogOut className="h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
